import { PURCHASER_API_BASE_URL, getHeaders } from "../Constants";

export type SourceGroundingResponse = {
    sourceId: number;
    mailboxId: number;
    mailboxPublicId: string | null;
    communicationId: number;
    emailId: number;
    fileUploadId: number;
    internalFilename: string | null;
    originalFilename: string | null;
    /**
     * LLM hint extracted at fact-creation time. Kept for back-compat. Prefer
     * `sourceLocators[0].page` when `kind === "bbox"` — that's the
     * resolver-authoritative page.
     */
    pageNumber: number | null;
    /**
     * Where this source was extracted from.
     * - "attachment": the fileUploadId points to a real attachment (PDF, XLSX, …)
     *   that can be opened in the document viewer.
     * - "email_body": the fileUploadId points to the raw .eml file. The
     *   document viewer can't render this — only "Open thread" is useful.
     */
    sourceType: "attachment" | "email_body";
    sourceLocators: SourceLocator[];
    locatorStatus: LocatorStatus;
    /** True iff /uploads/:id/:b64/layout will return a 200. */
    layoutAvailable: boolean;
};

/**
 * Where a single extracted fact lives inside its source file. The shape varies
 * by file type: PDFs/images get pixel geometry, spreadsheets get a cell pointer,
 * email bodies get a character offset. The discriminator is `kind`.
 *
 * Locator arrays returned alongside a source are ordered: index 0 is the
 * primary block, 1+ are secondary blocks (e.g. a table cell + its caption).
 * Most overlay implementations only need the primary.
 */
export type SourceLocator =
    | {
    kind: "bbox";
    page: number;
    bbox: [number, number, number, number];
    polygon?: [number, number][];
    blockId: string;
    blockType: string;
    sectionHierarchy?: Record<string, unknown>;
    score: number;
    method: "substring" | "sentence" | "trigram";
}
    | {
    kind: "cell";
    sheet: string;
    blockId: string;
    startRow: number;
    startCol: number;
    endRow: number;
    endCol: number;
    tableIndex?: number;
    score: number;
    method: "substring" | "sentence" | "trigram";
}
    | {
    kind: "char_offset";
    start: number;
    end: number;
}
    | {
    kind: "block_id";
    blockId: string;
    page?: number;
};

/**
 * Whether the resolver was able to pin this source to its origin geometry.
 * - "resolved": sourceLocators is non-empty and ready to render.
 * - "unresolved": pre-migration row or resolver miss; render the citation card
 *   without an overlay.
 * - "not_applicable": email-body sources where the thread *is* the source.
 */
export type LocatorStatus = "resolved" | "unresolved" | "not_applicable";


/**
 * One Datalab page's pixel dimensions. Use as the SVG `viewBox` for any
 * overlay that draws into this page's coordinate space.
 */
export type FileLayoutPage = {
    /** 0-indexed; matches SourceLocator.page when kind === "bbox". */
    page: number;
    width: number;
    height: number;
};

/**
 * One Datalab-extracted region on a page. The `id` is the same handle
 * SourceLocator.blockId references (e.g. "/page/0/Text/1"), which lets the FE
 * cross-link a chunk to any source that was extracted from inside it.
 */
export type FileLayoutBlock = {
    id: string;
    blockType: string;
    page: number;
    bbox: [number, number, number, number];
    polygon?: [number, number][];
};

export type FileLayoutResponse = {
    fileUploadId: number;
    layoutVersion: string;
    fetchedAt: string;
    pages: FileLayoutPage[];
    blocks: FileLayoutBlock[];
};

/**
 * 404 reasons surfaced by /uploads/:id/:b64/layout. Distinguishes "we never
 * could parse this format" (hide overlay UI entirely) from "we just don't have
 * a payload yet" (graceful degradation; future async resolution may populate it).
 */
export type FileLayoutMissing =
    | { reason: "not_layout_capable" }
    | { reason: "no_layout" };

export type BatchGroundingResponse = {
    groundings: SourceGroundingResponse[];
    /** ids that didn't resolve (deleted, wrong RFQ, wrong team). */
    notFound: number[];
};

/** Server-enforced cap; chunk on the FE side if a caller ever exceeds it. */
export const BATCH_GROUNDING_MAX_IDS = 50;

export type FileSourceItem = {
    /** document_memory.id; matches citation sourceId. */
    sourceId: number;
    summary: string;
    pageNumber: number | null;
    confidence: number;
    sourceLocators: SourceLocator[];
    locatorStatus: LocatorStatus;
};

export type FileSourcesResponse = {
    fileUploadId: number;
    /** Hint: true if /uploads/:id/:b64/layout will return 200. */
    layoutAvailable: boolean;
    sources: FileSourceItem[];
};

export default {
    /**
     * Geometry of a parsed file. Returns null on 404 with the discriminated
     * reason so the FE can branch on graceful-degradation vs hide-overlay-UI.
     */
    getFileLayout: async (
        id: number | string,
        b64: string,
    ): Promise<FileLayoutResponse | FileLayoutMissing | null> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/uploads/${id}/${b64}/layout`, {
            headers: getHeaders(),
        });
        if (res.status === 404) {
            const body = await res.json().catch(() => null);
            if (body && (body.reason === "not_layout_capable" || body.reason === "no_layout")) {
                return body as FileLayoutMissing;
            }
            return null;
        }
        if (!res.ok) return null;
        return res.json();
    },

    getFileSources: (id: number | string, b64: string): Promise<FileSourcesResponse> =>
        fetch(`${PURCHASER_API_BASE_URL}/uploads/${id}/${b64}/sources`, {
            headers: getHeaders(),
        }).then(res => res.json()),

    getSourceGrounding: (rfqPublicId: string, sourceId: number): Promise<SourceGroundingResponse | null> =>
        fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${rfqPublicId}/bid-leveling/sources/${sourceId}/grounding`, { headers: getHeaders() })
            .then(async res => {
                if (!res.ok) return null;
                return res.json().catch(() => null);
            })
            .catch(() => null),

    getBatchSourceGrounding: (rfqPublicId: string, sourceIds: number[]): Promise<BatchGroundingResponse> => {
        const ids = sourceIds.join(",");
        return fetch(`${PURCHASER_API_BASE_URL}/v2/rfqs/${rfqPublicId}/bid-leveling/grounding?ids=${ids}`, { headers: getHeaders() })
            .then(async res => {
                if (!res.ok) {
                    return { groundings: [], notFound: sourceIds };
                }
                const data = await res.json().catch(() => null);
                if (!data || !Array.isArray(data.groundings)) {
                    return { groundings: [], notFound: sourceIds };
                }
                return data as BatchGroundingResponse;
            })
            .catch(() => ({ groundings: [], notFound: sourceIds }));
    },
};
